import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useInView, useScroll, useTransform } from "framer-motion";
import { Menu, X, ArrowRight, Mail, Phone, MapPin, ChevronDown, Hammer, Lightbulb, Truck, Users, Sparkles, Clock, ArrowUpRight } from "lucide-react";
import "@/App.css";
import HirePage from "@/HirePage";
import BehindTheBuildsPage from "@/BehindTheBuildsPage";

// Actual Logo from brand
const LOGO_URL = "https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/y8za28e2_Coming%20soon%20%281%29.svg";

// Actual portfolio project images - working URLs
const portfolioProjects = [
  {
    id: 1,
    title: "Character-Led Scenic Entrance",
    category: "facades",
    description: "Character-led environmental installation with textured fur and leopard pattern detailing. Features LED glowing eyes for dramatic impact.",
    image: "/images/featured/scenic-entrance.jpg",
    specs: { height: "4.2m", width: "3.8m", lighting: "LED eyes" },
    size: "medium"
  },
  {
    id: 2,
    title: "Performance Platform",
    category: "platforms",
    description: "Mirrored platform engineered to function as both sculptural centrepiece and operational stage element.",
    image: "/images/featured/performance-platform.jpg",
    specs: { type: "Mirror tiles", lighting: "Purple LED", use: "Club/Event" },
    size: "medium"
  },
  {
    id: 3,
    title: "6M Illuminated Press Backdrop Environment",
    category: "backdrops",
    description: "Large illuminated press backdrop with recessed branded panels. Glowing effect creates depth on red carpet setting.",
    image: "/images/featured/press-backdrop.jpg",
    specs: { width: "6m", panels: "Multiple", lighting: "Pink/Purple" },
    size: "medium"
  },
  {
    id: 4,
    title: "Performance Platform Environment",
    category: "platforms",
    description: "Oversized character performance platform with illuminated eyes and detailed fur texturing.",
    image: "https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/jj6wf56z_DJ%20booth%20labubu.jpg",
    specs: { type: "Character build", lighting: "LED eyes", brand: "Custom" },
    size: "medium"
  },
  {
    id: 5,
    title: "Cowboy Hat Touring Engagement Piece",
    category: "touring",
    description: "Oversized branded cowboy hat with crystal detailing. Transport-ready activation piece designed for repeat use across multiple venues.",
    image: "https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/trs1as8r_WhatsApp%20Image%202026-02-17%20at%201.15.01%20PM%20%281%29.jpeg",
    specs: { type: "Touring", material: "Polystyrene", brand: "Custom" },
    size: "medium"
  },
  {
    id: 6,
    title: "Mirrored Branded Overhead Installation",
    category: "brand-activation",
    description: "Large mirrored branded logo suspended as striking overhead feature. Reflects ambient light for high-impact brand presence.",
    image: "https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/ty80grw4_beposke%20polystyrene.png",
    specs: { material: "Mirror tiles", mount: "Ceiling", brand: "Custom" },
    size: "medium"
  },
  {
    id: 7,
    title: "Silk Heart Installation",
    category: "brand-activation",
    description: "Large-scale outdoor heart installation intricately decorated with rouged silk fabric. Creates stunning photo moments for brand activations.",
    image: "https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/i2144qgs_Screenshot%202026-02-02%20200728.png",
    specs: { type: "Silk fabric", location: "Outdoor", style: "Heart motif" },
    size: "medium"
  },
  {
    id: 8,
    title: "Outdoor Scenic Feature",
    category: "outdoor",
    description: "Large freestanding vintage boombox design in red with black accents. Integrated LED perimeter lighting for strong nighttime visibility.",
    image: "https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/9kv4wty3_Boombox%20Red.png",
    specs: { scale: "Oversized", location: "Outdoor", lighting: "LED perimeter" },
    size: "medium"
  },
  {
    id: 9,
    title: "Illuminated Suspended Brand Feature",
    category: "brand-activation",
    description: "Custom neon-style racetrack silhouette suspended overhead. Vibrant red illumination creates high-energy atmosphere.",
    image: "https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/p7glj2fy_F1%20Racetrack%20light.png",
    specs: { type: "Neon LED", color: "Red", mount: "Suspended" },
    size: "small"
  },
  {
    id: 10,
    title: "Illuminated Feature Display Unit",
    category: "brand-activation",
    description: "Bespoke illuminated display case with real roses. Interactive brand moment for memorable guest experiences.",
    image: "https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/lpwt3o5m_WhatsApp%20Image%202025-09-23%20at%2013.59.42_3e4f2a3a.jpg",
    specs: { type: "Display case", lighting: "Pink LED", feature: "Interactive" },
    size: "small"
  },
  {
    id: 11,
    title: "Touring Brand Engagement Piece",
    category: "touring",
    description: "Branded bottle display designed as a touring activation piece. Transport-ready for repeat use across multiple venues and celebrations.",
    image: "https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/gn8fvdam_F1%20Bottle%20show%20prop.png",
    specs: { type: "Bottle display", use: "Touring", design: "Custom branded" },
    size: "small"
  },
  {
    id: 12,
    title: "Mirrored Branded Overhead Installation",
    category: "brand-activation",
    description: "Large mirrored branded installation suspended overhead as a striking brand activation centrepiece. Reflects ambient light for high-impact visual presence.",
    image: "https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/n28ixrs0_194gou%20mirror%20ball%20DJ%20booth.png",
    specs: { type: "Suspended", material: "Mirror", feature: "Overhead installation" },
    size: "small"
  }
];

const services = [
  {
    icon: Hammer,
    title: "Bespoke Fabrication",
    description: "Custom-built scenic elements and installations tailored to your exact creative vision. CNC routing and expert carpentry.",
    detail: "CNC routing, carpentry"
  },
  {
    icon: Lightbulb,
    title: "LED Integration",
    description: "Integrated lighting systems and electrical installations for stunning visual impact. Programmable and DMX compatible.",
    detail: "Programmable, DMX compatible"
  },
  {
    icon: Truck,
    title: "Touring Solutions",
    description: "Modular, transport-ready builds designed for repeat use and rapid deployment. Flight case ready with quick assembly.",
    detail: "Flight case ready, quick assembly"
  },
  {
    icon: Users,
    title: "On-Site Installation",
    description: "Professional installation teams with full RAMS and production documentation. Health & safety compliant.",
    detail: "Health & safety compliant"
  },
  {
    icon: Sparkles,
    title: "Scenic Design",
    description: "Large-scale scenic facades and environmental installations that transform spaces. Visuals included.",
    detail: "Visuals included"
  },
  {
    icon: Clock,
    title: "Fast Turnaround",
    description: "Efficient project delivery without compromising on quality or safety. Quick turnaround available depending on scope.",
    detail: "Turnaround depends on scope"
  }
];

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/portfolio", label: "Portfolio" },
    { to: "/hire", label: "Hire" },
    { to: "/behind-the-builds", label: "Behind The Builds" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" }
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-light py-3" : "bg-transparent py-5"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" className="relative z-10" data-testid="logo-link">
          <img 
            src={LOGO_URL} 
            alt="MT.LOOP" 
            className="h-10 md:h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-xs uppercase tracking-[0.2em] transition-all duration-300 hover:text-[#007e7a] ${
                location.pathname === link.to ? "text-[#007e7a] font-semibold" : "text-[#174c4f]/70"
              }`}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="btn-primary ml-4"
            data-testid="nav-start-project-btn"
          >
            Start a Project
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-[#174c4f] p-2"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-toggle"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-light"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-lg uppercase tracking-[0.15em] ${
                    location.pathname === link.to ? "text-[#007e7a]" : "text-[#174c4f]/70"
                  }`}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="btn-primary text-center mt-4"
                data-testid="mobile-start-project-btn"
              >
                Start a Project
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Section Reveal Component
const RevealSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Home Page
const HomePage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  // Hero carousel — finished MT.LOOP builds
  const heroImages = [
    { src: "/images/hero/champagne-wall.jpg", caption: "Illuminated Brand Wall" },
    { src: "/images/hero/boombox-red.jpg", caption: "Giant Boombox Build" },
    { src: "/images/hero/disco-booth.jpg", caption: "Mirror Ball DJ Booth" },
    { src: "/images/hero/bottle-show.jpg", caption: "Bottle Show Piece" },
    { src: "/images/hero/character-booth.jpg", caption: "Character DJ Booth" }
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [heroImages.length, currentImageIndex]);

  return (
    <div data-testid="home-page" className="pattern-bg">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <motion.div style={{ y }} className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(https://raw.githubusercontent.com/bep-tech/llm-data-extraction/main/images/page_6_image_2.png)`,
            }}
          />
          <div className="hero-overlay absolute inset-0" />
        </motion.div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 border border-[#007e7a]/10 rounded-full hidden lg:block" />
        <div className="absolute bottom-40 left-20 w-20 h-20 bg-[#ffd6a5]/20 rounded-full hidden lg:block" />

        {/* Content */}
        <motion.div style={{ opacity }} className="relative z-10 w-full">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="max-w-xl">
                {/* Tech Label */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="flex items-center gap-4 mb-8"
                >
                  <div className="w-12 h-[2px] bg-[#007e7a]" />
                  <span className="tech-label">Bespoke Fabrication & Scenic Design</span>
                </motion.div>

                {/* Main Title */}
                <motion.h1
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                  className="text-5xl sm:text-6xl md:text-7xl font-bold leading-[0.95] mb-8"
                  data-testid="hero-title"
                >
                  Fabricating
                  <br />
                  <span className="text-[#007e7a] inline-block mt-2">Ambitious</span>
                  <br />
                  <span className="inline-block mt-2">Ideas</span>
                </motion.h1>

                {/* Accent Line */}
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: 80 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="h-1 bg-[#ffd6a5] mb-8"
                />

                {/* Bold Statements */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className="max-w-xl mb-10"
                >
                  <p className="text-lg md:text-xl font-semibold text-[#174c4f] leading-snug mb-4" data-testid="hero-bold-statement">
                    Bespoke Fabrication for Retail, Hospitality &amp; Experiential Projects.
                  </p>
                  <p className="text-base md:text-lg text-[#789994] leading-relaxed" data-testid="hero-supporting-statement">
                    From concept to installation, we create unforgettable spaces, displays and brand activations.
                  </p>
                </motion.div>

                {/* CTAs */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-3" data-testid="hero-start-project-btn">
                    Start a Project <ArrowRight size={18} />
                  </Link>
                  <Link to="/portfolio" className="btn-secondary inline-flex items-center justify-center gap-3" data-testid="hero-view-work-btn">
                    View Our Work
                  </Link>
                </motion.div>
              </div>

              {/* Right side - Featured Image Carousel */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                data-testid="hero-carousel"
              >
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#007e7a]/30" />
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={currentImageIndex}
                        src={heroImages[currentImageIndex].src}
                        alt={heroImages[currentImageIndex].caption}
                        className="w-full h-full object-cover"
                        initial={{ opacity: 0, scale: 1.06 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ opacity: { duration: 0.8 }, scale: { duration: 4.5, ease: "linear" } }}
                      />
                    </AnimatePresence>
                    {/* Caption */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent pt-14 pb-5 px-6 pointer-events-none">
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={currentImageIndex}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.5 }}
                          className="text-white text-sm md:text-base font-medium tracking-wide uppercase"
                          data-testid="hero-carousel-caption"
                        >
                          {heroImages[currentImageIndex].caption}
                        </motion.span>
                      </AnimatePresence>
                    </div>
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#ffd6a5]/30" />
                  
                  {/* Slide indicators */}
                  <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 flex gap-2">
                    {heroImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        aria-label={`Show slide ${index + 1}`}
                        data-testid={`hero-carousel-dot-${index}`}
                        className={`h-2 rounded-full transition-all ${
                          index === currentImageIndex ? "bg-[#007e7a] w-6" : "bg-[#789994]/40 w-2 hover:bg-[#789994]/70"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-3 text-[#789994] z-10"
        >
          <span className="tech-label">Scroll</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ChevronDown size={20} />
          </motion.div>
        </motion.div>
      </section>

      {/* Featured Projects */}
      <section className="py-28 md:py-36 px-6 md:px-12" data-testid="featured-projects-section">
        <div className="max-w-7xl mx-auto">
          <RevealSection>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-8 h-[2px] bg-[#007e7a]" />
                  <span className="tech-label">Featured Work</span>
                </div>
                <h2 className="text-4xl md:text-6xl font-bold">Recent Projects</h2>
              </div>
              <Link 
                to="/portfolio" 
                className="text-[#007e7a] uppercase tracking-[0.15em] text-sm flex items-center gap-3 hover:gap-5 transition-all group font-semibold"
                data-testid="view-all-projects-link"
              >
                View All Projects 
                <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
              </Link>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioProjects.slice(0, 6).map((project, index) => (
              <RevealSection key={project.id}>
                <motion.div
                  className="group relative card-light overflow-hidden"
                  data-testid={`featured-project-${project.id}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={project.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-50" />
                    <img
                      src={project.image}
                      alt={project.title}
                      className="relative w-full h-full object-contain"
                    />
                  </div>

                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#174c4f] via-[#174c4f]/60 to-transparent opacity-0 group-hover:opacity-90 transition-opacity duration-500" />

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500">
                    <span className="tech-label text-[#ffd6a5] mb-3 block opacity-0 group-hover:opacity-100 transition-opacity duration-500">{project.category}</span>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">{project.title}</h3>
                    <p className="text-white/80 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                      {project.description}
                    </p>
                  </div>

                  {/* Category label always visible */}
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5">
                    <span className="tech-label text-[#007e7a] text-xs">{project.category}</span>
                  </div>
                </motion.div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* Hire Teaser Strip */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#174c4f]" data-testid="hire-teaser-section">
        <div className="max-w-7xl mx-auto">
          <RevealSection>
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1 text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start gap-4 mb-4">
                  <div className="w-8 h-[2px] bg-[#ffd6a5]" />
                  <span className="tech-label text-[#ffd6a5]">Available For Hire</span>
                </div>
                <h2 className="text-base md:text-lg font-semibold text-white uppercase tracking-[0.15em] mb-3">
                  Statement Pieces, Ready To Hire
                </h2>
                <p className="text-white/70 text-sm md:text-base max-w-xl mx-auto lg:mx-0 leading-relaxed">
                  Show-stopping scenic builds — customisable, delivered and installed by the MT.LOOP team.
                </p>
              </div>
              <div className="hidden md:flex gap-3">
                {[
                  "https://customer-assets-0z36b82j.emergentagent.net/job_78cdb6a7-1fac-4a3c-9b03-735d8ee52d6a/artifacts/durrg3r2_Christmas%20Boombox.png",
                  "https://customer-assets-0z36b82j.emergentagent.net/job_78cdb6a7-1fac-4a3c-9b03-735d8ee52d6a/artifacts/jyge14dm_Labu1.png",
                  "https://customer-assets-0z36b82j.emergentagent.net/job_78cdb6a7-1fac-4a3c-9b03-735d8ee52d6a/artifacts/rmcj0va8_Pink%20Heart%20Photo%20Op.png"
                ].map((img, i) => (
                  <Link key={i} to="/hire" className="w-24 h-24 lg:w-28 lg:h-28 overflow-hidden border border-white/20 hover:border-[#ffd6a5] transition-colors">
                    <img src={img} alt="Hire piece" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </Link>
                ))}
              </div>
              <Link
                to="/hire"
                className="btn-primary inline-flex items-center justify-center gap-3 flex-shrink-0"
                data-testid="hire-teaser-btn"
              >
                View Hire Range <ArrowRight size={18} />
              </Link>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-28 md:py-36 px-6 md:px-12 bg-white" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto">
          <RevealSection>
            <div className="text-center mb-20">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-8 h-[2px] bg-[#007e7a]" />
                <span className="tech-label">What We Do</span>
                <div className="w-8 h-[2px] bg-[#007e7a]" />
              </div>
              <h2 className="text-4xl md:text-6xl font-bold mb-6">Our Capabilities</h2>
              <p className="text-[#789994] max-w-2xl mx-auto text-lg">
                From concept to installation, we deliver end-to-end fabrication solutions
              </p>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 3).map((service, index) => (
              <RevealSection key={service.title}>
                <div 
                  className="card-light p-8 md:p-10 h-full group"
                  data-testid={`service-card-${index}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 bg-[#e8f9f7] flex items-center justify-center group-hover:bg-[#007e7a] transition-colors duration-500">
                      <service.icon className="w-7 h-7 text-[#007e7a] group-hover:text-white transition-colors duration-500" />
                    </div>
                    <span className="project-number text-3xl">0{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                  <p className="text-[#789994] mb-6 leading-relaxed">{service.description}</p>
                  <span className="tech-label text-xs">{service.detail}</span>
                </div>
              </RevealSection>
            ))}
          </div>

          <RevealSection className="text-center mt-14">
            <Link 
              to="/services" 
              className="btn-secondary inline-flex items-center gap-3"
              data-testid="view-all-services-btn"
            >
              All Services <ArrowRight size={16} />
            </Link>
          </RevealSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 md:py-36 px-6 md:px-12 warm-accent-bg" data-testid="cta-section">
        <div className="max-w-7xl mx-auto">
          <RevealSection>
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
              <div className="max-w-2xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-8 h-[2px] bg-[#174c4f]" />
                  <span className="tech-label text-[#174c4f]">Let's Collaborate</span>
                </div>
                <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                  Let's Build Something
                  <br />
                  <span className="text-[#007e7a]">Extraordinary</span>
                </h2>
                <p className="text-lg text-[#174c4f]/70 leading-relaxed">
                  From concept to installation, we bring your ambitious ideas to life 
                  with precision engineering and creative excellence.
                </p>
              </div>
              <Link 
                to="/contact" 
                className="bg-[#174c4f] text-white px-12 py-6 uppercase tracking-[0.15em] text-sm font-semibold flex items-center gap-4 hover:bg-[#007e7a] transition-colors"
                data-testid="cta-start-project-btn"
              >
                Start a Project <ArrowRight size={22} />
              </Link>
            </div>
          </RevealSection>
        </div>
      </section>
    </div>
  );
};

// Portfolio Page
const PortfolioPage = () => {
  const [filter, setFilter] = useState("all");
  const categories = ["all", "facades", "platforms", "backdrops", "outdoor", "touring", "brand-activation"];

  const filteredProjects = filter === "all" 
    ? portfolioProjects 
    : portfolioProjects.filter(p => p.category === filter);

  return (
    <div className="pt-28 pb-28 px-6 md:px-12 pattern-bg min-h-screen" data-testid="portfolio-page">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-8 h-[2px] bg-[#007e7a]" />
            <span className="tech-label">Our Work</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Portfolio</h1>
          <p className="text-lg text-[#789994] max-w-2xl mb-14 leading-relaxed">
            Explore our collection of bespoke fabrications, scenic installations, 
            and brand activations engineered to make an impact.
          </p>
        </RevealSection>

        {/* Filter */}
        <RevealSection className="mb-14">
          <div className="flex flex-wrap gap-3" data-testid="portfolio-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-5 py-3 uppercase tracking-[0.15em] text-xs transition-all duration-300 ${
                  filter === cat 
                    ? "bg-[#007e7a] text-white" 
                    : "bg-white border border-[#c5e5e0] text-[#789994] hover:text-[#174c4f] hover:border-[#007e7a]"
                }`}
                data-testid={`filter-${cat}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </RevealSection>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="wait">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative card-light overflow-hidden"
                data-testid={`portfolio-project-${project.id}`}
              >
                <div className="relative h-[320px] overflow-hidden">
                  <img src={project.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-50" />
                  <img
                    src={project.image}
                    alt={project.title}
                    className="relative w-full h-full object-contain"
                  />
                </div>

                <div className="absolute inset-0 bg-gradient-to-t from-[#174c4f] via-[#174c4f]/60 to-transparent opacity-0 group-hover:opacity-90 transition-opacity duration-500" />

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500">
                  <span className="tech-label text-[#ffd6a5] mb-3 block opacity-0 group-hover:opacity-100 transition-opacity duration-500">{project.category}</span>
                  <h3 className="text-2xl font-bold text-white mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">{project.title}</h3>
                  <p className="text-white/80 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {project.description}
                  </p>
                </div>

                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5">
                  <span className="tech-label text-[#007e7a] text-xs">{project.category}</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// Services Page
const ServicesPage = () => {
  return (
    <div className="pt-28 pb-28 px-6 md:px-12 pattern-bg" data-testid="services-page">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-8 h-[2px] bg-[#007e7a]" />
            <span className="tech-label">What We Offer</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Services</h1>
          <p className="text-lg text-[#789994] max-w-2xl mb-20 leading-relaxed">
            From initial concept to final installation, we deliver end-to-end 
            fabrication solutions engineered for impact.
          </p>
        </RevealSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-28">
          {services.map((service, index) => (
            <RevealSection key={service.title}>
              <motion.div 
                className="card-light p-8 md:p-10 h-full group"
                data-testid={`service-item-${index}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 bg-[#e8f9f7] flex items-center justify-center group-hover:bg-[#007e7a] transition-colors duration-500">
                    <service.icon className="w-7 h-7 text-[#007e7a] group-hover:text-white transition-colors duration-500" />
                  </div>
                  <span className="project-number text-4xl">0{index + 1}</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-[#789994] leading-relaxed mb-6">{service.description}</p>
                <span className="tech-label">{service.detail}</span>
              </motion.div>
            </RevealSection>
          ))}
        </div>

        {/* Process Section */}
        <RevealSection>
          <div className="border-t border-[#c5e5e0] pt-28">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-8 h-[2px] bg-[#007e7a]" />
              <span className="tech-label">How We Work</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-20">Our Process</h2>
          </div>
        </RevealSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { num: "01", title: "Brief", desc: "Understanding your vision, requirements and technical constraints" },
            { num: "02", title: "Design", desc: "Creating detailed designs, 3D visualisations and technical drawings" },
            { num: "03", title: "Build", desc: "Expert fabrication in our Leicester workshop with quality control" },
            { num: "04", title: "Install", desc: "Professional on-site installation with full documentation" }
          ].map((step, index) => (
            <RevealSection key={step.num}>
              <div className="relative group" data-testid={`process-step-${index}`}>
                <div className="project-number text-6xl mb-4">{step.num}</div>
                <div className="w-12 h-[2px] bg-[#007e7a] mb-4 group-hover:w-20 transition-all duration-500" />
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-[#789994] text-sm leading-relaxed">{step.desc}</p>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>
    </div>
  );
};

// About Page
const AboutPage = () => {
  return (
    <div className="pt-28 pb-28 px-6 md:px-12 pattern-bg" data-testid="about-page">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-8 h-[2px] bg-[#007e7a]" />
            <span className="tech-label">Who We Are</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-8">About MT.Loop</h1>
        </RevealSection>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 mb-28">
          <RevealSection>
            <div className="space-y-8">
              <p className="text-xl md:text-2xl text-[#174c4f] leading-relaxed">
                MT.Loop is a family-based fabrication company 
                with a passion for bringing ambitious ideas to life.
              </p>
              <p className="text-[#789994] leading-relaxed text-lg">
                With over 10 years of experience in the building industry, we've developed 
                expertise in creating large-scale scenic installations, custom props, 
                and brand activations that make an impact.
              </p>
              <p className="text-[#789994] leading-relaxed text-lg">
                From our workshop in Leicester, we design, fabricate, and install 
                bespoke creations for events, exhibitions, and brand experiences across the UK.
              </p>
              <div className="flex items-center gap-4 pt-4">
                <div className="w-16 h-[3px] bg-[#ffd6a5]" />
                <span className="text-[#007e7a] uppercase tracking-[0.15em] text-sm font-semibold">Engineering Creativity</span>
              </div>
            </div>
          </RevealSection>

          <RevealSection>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_43595472-05a0-4bd7-8c31-5b8f461f3319/artifacts/2tdhlcqm_WhatsApp%20Image%202026-02-17%20at%201.15.01%20PM.jpeg"
                alt="MT.Loop Brand Pattern"
                className="w-full h-[450px] object-cover"
              />
            </div>
          </RevealSection>
        </div>

        {/* Founders - Combined into one */}
        <RevealSection>
          <div className="border-t border-[#c5e5e0] pt-28">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-8 h-[2px] bg-[#007e7a]" />
              <span className="tech-label">The Team</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-20">Meet the Founders</h2>
          </div>
        </RevealSection>

        <RevealSection>
          <div className="max-w-2xl">
            <div 
              className="card-light p-8 md:p-12 group"
              data-testid="founders-card"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="w-24 h-24 bg-[#007e7a] flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">J & L</span>
                </div>
              </div>
              <h3 className="text-3xl font-bold mb-2">Joe & Laura</h3>
              <span className="tech-label mb-8 block">Founders & Creators</span>
              <p className="text-[#789994] leading-relaxed text-lg mb-6">
                With over 10 years combined experience in the building industry, Joe and Laura 
                founded MT.Loop with a shared love of making things and bringing ambitious ideas to life.
              </p>
              <p className="text-[#789994] leading-relaxed">
                As a family-based company, we bring passion and personal attention to every project, 
                treating each build as if it were our own. Our hands-on approach ensures quality 
                craftsmanship and creative solutions that exceed expectations.
              </p>
            </div>
          </div>
        </RevealSection>

        {/* Values */}
        <RevealSection>
          <div className="mt-28 pt-28 border-t border-[#c5e5e0]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {[
                { title: "Quality", desc: "Craftsmanship at the heart of everything we build" },
                { title: "Innovation", desc: "Pushing boundaries in fabrication and engineering" },
                { title: "Reliability", desc: "Delivering on time, every time, without compromise" }
              ].map((value, index) => (
                <div key={value.title} className="text-center group" data-testid={`value-${index}`}>
                  <div className="w-16 h-[3px] bg-[#007e7a] mx-auto mb-6 group-hover:w-24 group-hover:bg-[#ffd6a5] transition-all duration-500" />
                  <h3 className="text-2xl font-bold text-[#007e7a] mb-4">{value.title}</h3>
                  <p className="text-[#789994] leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </RevealSection>
      </div>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  return (
    <div className="pt-28 pb-28 px-6 md:px-12 pattern-bg min-h-screen" data-testid="contact-page">
      <div className="max-w-5xl mx-auto">
        <RevealSection>
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-8 h-[2px] bg-[#007e7a]" />
              <span className="tech-label">Get in Touch</span>
              <div className="w-8 h-[2px] bg-[#007e7a]" />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-4 leading-tight">
              Let's Talk
            </h1>
            <h1 className="text-5xl md:text-7xl font-bold text-[#007e7a]">
              About Your Project
            </h1>
          </div>
        </RevealSection>

        <RevealSection>
          <div className="max-w-3xl mx-auto text-center mb-20">
            <p className="text-lg md:text-xl text-[#174c4f] leading-relaxed mb-6">
              We're a friendly, family-run team and we love hearing about new ideas — 
              whether it's a fully-formed brief or just the first spark of a concept.
            </p>
            <p className="text-base md:text-lg text-[#789994] leading-relaxed mb-6">
              Every project we take on starts with a proper conversation. So drop us an email 
              or pick up the phone and tell us what you're planning. We'll listen, ask the right 
              questions, and come back with honest, practical thinking on how to bring it to life.
            </p>
            <p className="text-base md:text-lg text-[#789994] leading-relaxed">
              No forms, no automated replies — just a direct line to Joe &amp; Laura.
            </p>
          </div>
        </RevealSection>

        <RevealSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-16">
            <a
              href="mailto:LAURA@MTLOOP.CO.UK"
              className="group bg-white border border-[#c5e5e0] hover:border-[#007e7a] p-10 flex flex-col items-center text-center transition-all hover:shadow-lg"
              data-testid="contact-email"
            >
              <div className="w-16 h-16 bg-[#e8f9f7] border border-[#c5e5e0] group-hover:bg-[#007e7a] group-hover:border-[#007e7a] flex items-center justify-center mb-6 transition-all">
                <Mail className="w-6 h-6 text-[#007e7a] group-hover:text-white transition-colors" />
              </div>
              <span className="tech-label text-xs mb-3">Email us</span>
              <span className="text-[#174c4f] group-hover:text-[#007e7a] font-medium tracking-[0.05em] text-sm md:text-base break-all transition-colors">
                LAURA@MTLOOP.CO.UK
              </span>
            </a>

            <a
              href="tel:07979119771"
              className="group bg-white border border-[#c5e5e0] hover:border-[#007e7a] p-10 flex flex-col items-center text-center transition-all hover:shadow-lg"
              data-testid="contact-phone"
            >
              <div className="w-16 h-16 bg-[#e8f9f7] border border-[#c5e5e0] group-hover:bg-[#007e7a] group-hover:border-[#007e7a] flex items-center justify-center mb-6 transition-all">
                <Phone className="w-6 h-6 text-[#007e7a] group-hover:text-white transition-colors" />
              </div>
              <span className="tech-label text-xs mb-3">Give us a call</span>
              <span className="text-[#174c4f] group-hover:text-[#007e7a] font-medium tracking-[0.05em] text-base md:text-lg transition-colors">
                07979 119 771
              </span>
            </a>
          </div>
        </RevealSection>

        <RevealSection>
          <div className="max-w-3xl mx-auto pt-12 border-t border-[#c5e5e0]" data-testid="contact-address">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
              <div className="w-14 h-14 bg-white border border-[#c5e5e0] flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#007e7a]" />
              </div>
              <div>
                <span className="tech-label text-xs block mb-2">Workshop</span>
                <p className="text-[#174c4f] leading-relaxed">
                  1 Charter Street, The Kapital Building, Unit 2C<br />
                  Leicester, LE1 3UD
                </p>
              </div>
            </div>
          </div>
        </RevealSection>
      </div>
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-16 px-6 md:px-12 bg-[#174c4f] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <img 
              src={LOGO_URL} 
              alt="MT.LOOP" 
              className="h-10 w-auto object-contain mb-6 brightness-0 invert"
            />
            <p className="text-white/70 max-w-sm leading-relaxed mb-6">
              Fabricating ambitious ideas. Bespoke scenic 
              fabrication engineered for brands and creative agencies.
            </p>
            <div className="w-16 h-[2px] bg-[#ffd6a5]" />
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <h4 className="text-xs uppercase tracking-[0.25em] text-[#ffd6a5] mb-6">Quick Links</h4>
            <ul className="space-y-4">
              {[
                { label: "Portfolio", to: "/portfolio" },
                { label: "Hire", to: "/hire" },
                { label: "Behind The Builds", to: "/behind-the-builds" },
                { label: "Services", to: "/services" },
                { label: "About", to: "/about" },
                { label: "Contact", to: "/contact" }
              ].map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.to}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.25em] text-[#ffd6a5] mb-6">Contact</h4>
            <ul className="space-y-4 text-sm text-white/70">
              <li>
                <a href="mailto:LAURA@MTLOOP.CO.UK" className="hover:text-white transition-colors">
                  LAURA@MTLOOP.CO.UK
                </a>
              </li>
              <li>
                <a href="tel:07979119771" className="hover:text-white transition-colors">
                  07979 119 771
                </a>
              </li>
              <li>Leicester, UK</li>
            </ul>
          </div>
        </div>

        <div className="pt-10 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} MT.Loop. All rights reserved.
          </p>
          <p className="text-white/50 text-sm tracking-[0.1em]">
            Fabricating Ambitious Ideas
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="App min-h-screen bg-[#e8f9f7]">
      <BrowserRouter>
        <Navigation />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/hire" element={<HirePage />} />
            <Route path="/behind-the-builds" element={<BehindTheBuildsPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
